// Vehicle Scope Selector - Quick dropdown + full-screen picker
import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { useSettingsStore, getScopeDisplayText, type VehicleScope } from '@/store/settingsStore';
import { getVehicles } from '@/services/api';
import { VehiclePickerOverlay } from '@/components/vehicles/VehiclePickerOverlay';
import type { Vehicle } from '@/types';

export function VehicleScopeSelector() {
  const [isOpen, setIsOpen] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [expandedMake, setExpandedMake] = useState<string | null>(null);

  const {
    vehicleScope,
    setScopeToAll,
    setScopeToMake,
    setScopeToProject,
  } = useSettingsStore();

  // Load vehicles
  useEffect(() => {
    getVehicles().then(setVehicles).catch(console.error);
  }, []);

  // Group vehicles by make
  const makeGroups = useMemo(() => {
    const map = new Map<string, Vehicle[]>();
    for (const v of vehicles) {
      if (!map.has(v.make)) map.set(v.make, []);
      map.get(v.make)!.push(v);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([make, projects]) => ({ make, projects: projects.sort((a, b) => a.year - b.year) }));
  }, [vehicles]);

  const handleSelectAll = () => {
    setScopeToAll();
    setIsOpen(false);
  };

  const handleSelectMake = (make: string) => {
    setScopeToMake(make);
    setIsOpen(false);
  };

  const handleSelectProject = (vehicle: Vehicle) => {
    setScopeToProject(vehicle.id, vehicle.name, vehicle.make, vehicle.model);
    setIsOpen(false);
  };

  const openFullPicker = () => {
    setIsOpen(false);
    setIsPickerOpen(true);
  };

  return (
    <>
      <div className="relative">
        {/* Trigger Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={clsx(
            'flex items-center gap-2 px-3 py-2 rounded-lg',
            'bg-workshop-surface border border-workshop-border',
            'text-white hover:border-workshop-accent',
            'transition-all duration-200',
            isOpen && 'border-workshop-accent'
          )}
        >
          <ScopeIcon scope={vehicleScope} />
          <span className="text-sm font-medium max-w-[150px] truncate">
            {getScopeDisplayText(vehicleScope)}
          </span>
          <ChevronIcon className={clsx('w-4 h-4 transition-transform', isOpen && 'rotate-180')} />
        </button>

        {/* Quick Dropdown */}
        <AnimatePresence>
          {isOpen && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-40"
                onClick={() => setIsOpen(false)}
              />

              {/* Menu */}
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                className="absolute left-0 top-full mt-2 z-50 w-72 max-h-96 overflow-auto bg-workshop-bg border border-workshop-border rounded-xl shadow-2xl"
              >
                {/* Browse All - launches full-screen picker */}
                <button
                  onClick={openFullPicker}
                  className="w-full px-4 py-3 flex items-center gap-3 text-left border-b border-workshop-border hover:bg-workshop-surface transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-workshop-accent/20 flex items-center justify-center">
                    <ExpandIcon className="w-4 h-4 text-workshop-accent" />
                  </div>
                  <div className="flex-1">
                    <p className="text-workshop-accent font-medium">Browse All Projects</p>
                    <p className="text-xs text-workshop-text-muted">Full-screen view with images</p>
                  </div>
                </button>

                {/* All Projects */}
                <button
                  onClick={handleSelectAll}
                  className={clsx(
                    'w-full px-4 py-3 flex items-center gap-3 text-left border-b border-workshop-border',
                    'hover:bg-workshop-surface transition-colors',
                    vehicleScope.type === 'all' && 'bg-workshop-accent/10'
                  )}
                >
                  <div className="w-8 h-8 rounded-full bg-workshop-surface flex items-center justify-center">
                    <GridIcon className="w-4 h-4 text-workshop-text-muted" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">All Projects</p>
                    <p className="text-xs text-workshop-text-muted">{vehicles.length} vehicles</p>
                  </div>
                  {vehicleScope.type === 'all' && <CheckIcon className="w-5 h-5 text-workshop-accent" />}
                </button>

                {/* Makes with nested projects */}
                <div className="py-1">
                  {makeGroups.map(({ make, projects }) => (
                    <div key={make}>
                      <div className="flex items-center">
                        <button
                          onClick={() => setExpandedMake(expandedMake === make ? null : make)}
                          className={clsx(
                            'flex-1 px-4 py-2.5 flex items-center gap-3 text-left',
                            'hover:bg-workshop-surface transition-colors',
                            vehicleScope.type === 'make' && vehicleScope.make === make && 'bg-workshop-accent/10'
                          )}
                        >
                          <ChevronIcon
                            className={clsx(
                              'w-4 h-4 text-workshop-text-muted transition-transform',
                              expandedMake === make && 'rotate-90'
                            )}
                          />
                          <span className="text-white">{make}</span>
                          <span className="text-xs text-workshop-text-muted ml-auto">{projects.length}</span>
                        </button>
                        <button
                          onClick={() => handleSelectMake(make)}
                          className="px-3 py-2 text-xs text-workshop-accent hover:bg-workshop-accent/10 rounded transition-colors"
                          title={`Select all ${make}`}
                        >
                          All
                        </button>
                      </div>

                      {/* Projects under make */}
                      <AnimatePresence>
                        {expandedMake === make && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden bg-workshop-surface/30"
                          >
                            {projects.map((project) => (
                              <button
                                key={project.id}
                                onClick={() => handleSelectProject(project)}
                                className={clsx(
                                  'w-full pl-12 pr-4 py-2.5 flex items-center gap-3 text-left',
                                  'hover:bg-workshop-surface transition-colors',
                                  vehicleScope.type === 'project' &&
                                    vehicleScope.projectId === project.id &&
                                    'bg-workshop-accent/10'
                                )}
                              >
                                <CarIcon className="w-4 h-4 text-workshop-text-muted" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-white/80 text-sm truncate">
                                    {project.year} {project.model}
                                  </p>
                                </div>
                                {vehicleScope.type === 'project' &&
                                  vehicleScope.projectId === project.id && (
                                    <CheckIcon className="w-4 h-4 text-workshop-accent" />
                                  )}
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>

      {/* Full-screen picker overlay */}
      <VehiclePickerOverlay
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
      />
    </>
  );
}

function ScopeIcon({ scope }: { scope: VehicleScope }) {
  const iconClass = 'w-4 h-4 text-workshop-accent';
  switch (scope.type) {
    case 'all':
      return <GridIcon className={iconClass} />;
    case 'make':
      return <BuildingIcon className={iconClass} />;
    case 'model':
      return <TagIcon className={iconClass} />;
    case 'project':
      return <CarIcon className={iconClass} />;
  }
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function GridIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
    </svg>
  );
}

function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );
}

function TagIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
    </svg>
  );
}

function CarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 17h.01M16 17h.01M6 11h12M3 17h1a2 2 0 002-2v-2.5l1.5-3A2 2 0 019.3 8h5.4a2 2 0 011.8 1.1l1.5 3V15a2 2 0 002 2h1" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function ExpandIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
    </svg>
  );
}
